import React, { useEffect, useRef, useState } from 'react';
import { causalApi as api } from './causalApi';
import { clearClusterResults } from './clusterCache';

export default function CausalCleanup({ period, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [key, setKey] = useState(() => { try { return localStorage.getItem('llm-last-purge') || ''; } catch { return ''; } });
  const [job, setJob] = useState(null);
  const scope = useRef(period.query);
  const refresh = useRef(onDeleted);
  scope.current = period.query; refresh.current = onDeleted;
  useEffect(() => { setPreview(null); setConfirm(''); setError(''); }, [period.query]);
  useEffect(() => {
    if (!key) return;
    let live = true;
    let timer;
    const load = async () => {
      try {
        const result = await api(`purge/${encodeURIComponent(key)}`);
        if (!live) return;
        setJob(result);
        if (result.status === 'completed') {
          clearClusterResults();
          try { localStorage.removeItem('llm-last-purge'); } catch { /* Optional cache. */ }
          await refresh.current();
        } else timer = setTimeout(load, 2000);
      } catch (err) { if (live) { setError(err.message); timer = setTimeout(load, 5000); } }
    };
    load();
    return () => { live = false; clearTimeout(timer); };
  }, [key]);
  const check = async () => {
    const query = period.query;
    setBusy(true); setError(''); setConfirm('');
    try {
      const value = await api(`purge-preview?${query}`);
      if (scope.current === query) setPreview(value);
    } catch (err) { if (scope.current === query) setError(err.message); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    const query = period.query;
    setBusy(true); setError('');
    try {
      const value = await api('purge', { start_date: period.start, end_date: period.end, group_id: period.group || null, confirm: true });
      // Remember the operation even if the selected period changed during deletion.
      try { localStorage.setItem('llm-last-purge', value.id); } catch { /* Optional cache. */ }
      clearClusterResults(); setJob(value); setKey(value.id); setPreview(null); setConfirm('');
      await refresh.current();
    } catch (err) { if (scope.current === query) setError(err.message); }
    finally { setBusy(false); }
  };
  const running = Boolean(key && job?.status !== 'completed');
  return <section className="causal-panel mb-3" style={{ borderColor: '#e4b9bf' }}>
    <h3>Удаление результатов LLM</h3>
    <p>Период: <b>{period.start} — {period.end}</b>, обе даты включительно. Сообщество: <b>{period.group ? period.groups.find(row => String(row.group_id) === period.group)?.title || period.group : 'Все сообщества'}</b>.</p>
    <p className="small text-muted">Удаляются результаты, история ручных правок и задания LLM за этот период. Связанные карты распределения станут недействительными. Исходные посты, комментарии, справочники и история сессий сохраняются. Фильтры по темам и эмоциям не ограничивают удаление.</p>
    {error && <p className="text-danger" role="alert">{error}</p>}
    {job && <p role="status">{job.status === 'completed' ? 'Удаление завершено. Можно создать новую задачу анализа.' : 'Результаты удалены из PostgreSQL. Выполняется очистка полей в Mongo и кеша карт…'}{job.start_date && <> Период операции: {job.start_date} — {job.end_date}.</>}{job.error && <span className="d-block text-danger">{job.error} Очистка повторится автоматически.</span>}</p>}
    <button className="btn btn-sm btn-outline-secondary" disabled={busy || running || !period.ready} onClick={check}>Проверить объём удаления</button>
    {preview && <div className="mt-3">
      <p>Уникальных сообщений: <b>{preview.messages}</b>; версий анализа: <b>{preview.analyses}</b>; заданий: <b>{preview.jobs}</b>, из них в очереди: <b>{preview.pending}</b>, в работе: <b>{preview.processing}</b>.</p>
      {preview.processing > 0 ? <p className="text-danger">Приостановите задачи за этот период, дождитесь завершения текущего анализа и повторите проверку.</p> : <>
        <label>Для подтверждения введите УДАЛИТЬ<input className="form-control form-control-sm my-2" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="off" /></label>
        <button className="btn btn-sm btn-danger d-block" disabled={busy || running || confirm !== 'УДАЛИТЬ'} onClick={remove}>{busy ? 'Удаление…' : 'Удалить данные LLM за период'}</button>
      </>}
    </div>}
  </section>;
}
